/* eslint-disable no-duplicate-case */

// Permissions System
//
// With the large amount of user types and account plans, we created this
// permissions system to manage access to resources and/or actions.
//
// There are three many concepts in this file:
//  1. Policies
//  2. Discriminators
//  3. Checkers
//
// Policies are a description of the kinds of users which may access a resource
// or perform an action. They have three components: plan, user and type.
// 1. "plan" refers to the plan the user must have in order to be allowed
//    access.
// 2. "user" refers to the type/role of user which the user must have to be
//    allowed access.
// 3. "type" refers to the type of organization the user must be a part of
//    which is allowed access.
//
// Discriminators are components derived from the user structure which allows
// us to check a user against a policy.
//
// Checkers take a user object and a given action or resource describer, find
// the appropriate policy for that action or resource and then see if the user
// passes the policy. Some checkers in this file are:
//  - checkAction
//  - checkRoute
//
// Checkers are to be the main interface used by other modules across the
// project. The other concepts are to be used exclusively by this module.

import { PATH_DASHBOARD, PATH_TRAINING } from 'src/routes/paths';
import * as config from 'src/config';
import useAuth from 'src/hooks/useAuth';
import { useMemo } from 'react';
import { getPlanBySubscriptionProductId, plansWithCapability } from 'careit';
import { isBlockedByRecipientEin } from 'src/utils/einStatus';

export const PLAN_ID_SELECTOR = config.isDevelopment ? 'test' : 'live';

const NONE = [];

// plan — derived from packages/careit capability matrix
const FRESHCUE = ['freshcue'];
const FOOD_RUNS = plansWithCapability('foodRuns');
const FOODMOVER_FULL = plansWithCapability('proOps');
// Food-run routes (not home-delivery-only plans like Food Delivery Pro)
const FOODMOVER = FOOD_RUNS;
// Any ops plan (food runs and/or home delivery) plus basic/free helpers
const OPS = [
  ...new Set([
    ...FOOD_RUNS,
    ...plansWithCapability('homeDelivery'),
    ...FOODMOVER_FULL,
  ]),
];
const BASIC_OR_FOODMOVER = ['basic', ...OPS];
const BASIC_OR_FOODMOVER_FULL = ['basic', ...FOODMOVER_FULL];
const FREE = ['free', ...BASIC_OR_FOODMOVER];

/** True when the plan includes inventory (Basic or a full Food Mover plan). */
export const hasInventoryAccess = (plan) =>
  BASIC_OR_FOODMOVER_FULL.includes(plan);

/**
 * True when the plan is Basic or any ops (Food Mover) plan. `plan` is the
 * active-gated key from useAuth (non-active subscriptions resolve to 'free').
 */
export const hasBasicOrFoodMoverPlan = (plan) =>
  BASIC_OR_FOODMOVER.includes(plan);

// user
const SUPER_ADMIN = 'super-admin';
const ADMIN = 'admin';
const STAFF = 'staff';
const DRIVER = 'driver';

// type
const NPO = 'npo';
const BUSINESS = 'business';
const EXECUTIVE = 'executive';
const CORPORATE = 'corporate';
const ALL = [NPO, BUSINESS, CORPORATE];

const policyForPath = (path) => {
  // shorthand helper
  const p = (p) => path && path.startsWith(p);

  if (!path) {
    return {
      plan: FREE,
      user: [STAFF, ADMIN, DRIVER],
      type: [...ALL, EXECUTIVE, CORPORATE],
    };
  }

  switch (true) {
    // super admin only
    // (admin.marketplace covers the general/sales/listings/settings
    // sub-pages by prefix — mirroring how crm.root covers crm.settings; the
    // marketplace settings page additionally guards itself to owners
    // in-page, since menu hiding is not access control)
    case p(PATH_DASHBOARD.admin.overview):
    case p(PATH_DASHBOARD.admin.executiveUsers):
    case p(PATH_DASHBOARD.admin.viewExecutiveUsers('')):
    case p(PATH_DASHBOARD.admin.corporateAccounts):
    case p(PATH_DASHBOARD.admin.viewCorporateAccount('')):
    case p(PATH_DASHBOARD.admin.users):
    case p(PATH_DASHBOARD.admin.viewUser('')):
    case p(PATH_DASHBOARD.admin.orgs):
    case p(PATH_DASHBOARD.admin.viewOrg('')):
    case p(PATH_DASHBOARD.admin.approvalQueue):
    case p(PATH_DASHBOARD.admin.complaintsQueue):
    case p(PATH_DASHBOARD.admin.map):
    case p(PATH_DASHBOARD.admin.mergeOrgs):
    case p(PATH_DASHBOARD.admin.createOrg):
    case p(PATH_DASHBOARD.admin.subscriptions):
    case p(PATH_DASHBOARD.admin.freshcueNotifications):
    case p(PATH_DASHBOARD.admin.freshcueWaitlist):
    case p(PATH_DASHBOARD.admin.imports.root):
    case p(PATH_DASHBOARD.admin.deletions.root):
    case p(PATH_DASHBOARD.admin.inventory):
    case p(PATH_DASHBOARD.admin.viewInventory('')):
    case p(PATH_DASHBOARD.admin.cancellations):
    case p(PATH_DASHBOARD.admin.einStatus):
    case p('/dashboard/admin/ein-verification'):
    case p(PATH_DASHBOARD.admin.marketplace):
    case p(PATH_DASHBOARD.admin.crm.root):
    case p(PATH_DASHBOARD.admin.newsletters.root):
      return { plan: FREE, user: [SUPER_ADMIN], type: [BUSINESS] };

    case p(PATH_DASHBOARD.trainingEditor):
      return {
        plan: FOODMOVER_FULL,
        user: [STAFF, ADMIN, DRIVER],
        type: [NPO],
        orHasOrgFlag: 'is_safety_program_enabled',
      };

    case p(PATH_DASHBOARD.findDonations):
    case p(PATH_DASHBOARD.findItems.donations):
      return { plan: FREE, user: [STAFF, ADMIN, DRIVER], type: [NPO] };

    // Marketplace browsing is open to every org type — businesses buy
    // surplus too; purchasing itself is role-gated server-side.
    case p(PATH_DASHBOARD.findItems.marketplace):
      return {
        plan: FREE,
        user: [STAFF, ADMIN, DRIVER],
        type: [NPO, BUSINESS],
      };

    // Marketplace checkout is buyer-only: it mirrors the My Orders policy
    // below. It must be matched before the viewListing('') prefix case or
    // /marketplace/listings/:id/checkout would inherit the open listing
    // detail policy.
    case p(PATH_DASHBOARD.marketplace.viewListing('')) &&
      path.endsWith('/checkout'):
      return { plan: FREE, user: [STAFF, ADMIN], type: [NPO, BUSINESS] };

    // the donation and food run details pages handle permissions on their own
    case p(PATH_DASHBOARD.viewDonation('')):
    case p(PATH_DASHBOARD.marketplace.viewListing('')):
    case p(PATH_DASHBOARD.viewFoodRun('')):
    case p(PATH_DASHBOARD.viewOrg('')):
      return {
        plan: FREE,
        user: [STAFF, ADMIN, DRIVER, SUPER_ADMIN],
        type: [...ALL, CORPORATE],
      };

    case p(PATH_DASHBOARD.chatShortcut('')):
    case p(PATH_DASHBOARD.schedule.root):
      return {
        plan: FREE,
        user: [STAFF, ADMIN, DRIVER],
        type: [NPO, BUSINESS],
      };

    // donations - basic npo/business staff and admins, OR super admins
    case p(PATH_DASHBOARD.drivers):
      return {
        plan: FREE,
        user: [ADMIN],
        type: [NPO, BUSINESS],
      };
    case p(PATH_DASHBOARD.vehicle):
      return {
        plan: BASIC_OR_FOODMOVER_FULL,
        user: [STAFF, ADMIN],
        type: [NPO, BUSINESS],
      };
    case p(PATH_DASHBOARD.donations):
      return {
        plan: FREE,
        user: [DRIVER, STAFF, ADMIN, SUPER_ADMIN],
        type: [NPO, BUSINESS],
      };

    // basic npo - all users
    case p(PATH_DASHBOARD.myrescues):
      return {
        plan: FREE,
        user: [DRIVER, STAFF, ADMIN, SUPER_ADMIN],
        type: [NPO],
      };

    // rescues - basic npo staff and admins, OR super admins
    case p(PATH_DASHBOARD.rescues):
      return {
        plan: FREE,
        user: [DRIVER, STAFF, ADMIN, SUPER_ADMIN],
        type: [NPO],
      };

    // any npo - all users
    case p(PATH_DASHBOARD.myrescues):
    case p(PATH_DASHBOARD.incompleteRescues):
    case p(PATH_DASHBOARD.careitId):
      return {
        plan: FREE,
        user: [DRIVER, STAFF, ADMIN],
        type: [NPO],
      };

    // basic npo
    case p(PATH_DASHBOARD.rescues):
    case p(PATH_DASHBOARD.business):
    case p(PATH_DASHBOARD.editOrg('')):
    case p(PATH_DASHBOARD.incomingRequests):
      return { plan: FREE, user: [STAFF, ADMIN], type: [NPO] };
    // basic npo or business - staff or admin
    // (marketplace messages and the order details page mirror the My Orders
    // policy: any buyer or seller org member who can order can also message
    // about listings and open their orders; the order details endpoint
    // scopes data to buyer/seller participants server-side)
    case p(PATH_DASHBOARD.donations):
    case p(PATH_DASHBOARD.mydonations):
    case p(PATH_DASHBOARD.marketplace.myListings):
    case p(PATH_DASHBOARD.marketplace.myOrders):
    case p(PATH_DASHBOARD.marketplace.viewOrder('')):
    case p(PATH_DASHBOARD.marketplace.messages):
    case p(PATH_DASHBOARD.marketplace.incomingOrders):
    case p(PATH_DASHBOARD.marketplace.createListing):
    case p(PATH_DASHBOARD.marketplace.editListing('')):
      return { plan: FREE, user: [STAFF, ADMIN], type: [NPO, BUSINESS] };

    // foodmover npo
    case p(PATH_DASHBOARD.foodRun.myRuns):
    case p(PATH_DASHBOARD.foodRun.availableRuns):
    case p(PATH_DASHBOARD.foodRun.inProgress):
    case p(PATH_DASHBOARD.foodRun.runHistory):
      return { plan: FOODMOVER, user: [DRIVER, STAFF, ADMIN], type: [NPO] };
    // foodmover npo - staff and admin only
    case p(PATH_DASHBOARD.npo):
      return { plan: FOODMOVER, user: [STAFF, ADMIN, DRIVER], type: [NPO] };
    case p(PATH_DASHBOARD.foodRun.allRuns):
      return {
        plan: FOODMOVER,
        user: [STAFF, ADMIN, DRIVER, SUPER_ADMIN],
        type: [NPO],
      };
    case p(PATH_DASHBOARD.donationsReport):
      return {
        plan: FOODMOVER_FULL,
        user: [ADMIN],
        type: [NPO],
      };
    case p(PATH_DASHBOARD.foodRun.toBeReassigned):
      return {
        plan: FOODMOVER,
        user: [STAFF, ADMIN],
        type: [NPO],
      };
    // foodmover npo - admin only
    case p(PATH_DASHBOARD.schedule.donations):
    case p(PATH_DASHBOARD.customizeEmail):
      return { plan: FOODMOVER, user: [ADMIN], type: [NPO] };

    // admin, staff, driver of busines and npo
    case p(PATH_DASHBOARD.settings.documents):
      return {
        plan: FREE,
        user: [STAFF, ADMIN, DRIVER],
        type: [NPO, BUSINESS, CORPORATE],
      };

    // all users - admin
    case p(PATH_DASHBOARD.settings.business('')):
    case p(PATH_DASHBOARD.allPlans):
      return { plan: FREE, user: [ADMIN], type: ALL };
    // all users - admin incl executive
    case p(PATH_DASHBOARD.teams):
      return { plan: FREE, user: [ADMIN], type: [...ALL, EXECUTIVE] };

    case p(PATH_DASHBOARD.partnerships):
    case p(PATH_DASHBOARD.agreements):
    case p(PATH_DASHBOARD.agreement('')):
      return { plan: FREE, user: [STAFF, ADMIN], type: [NPO, BUSINESS] };

    // executive users - Staff/Admin access
    case p(PATH_DASHBOARD.executive.home):
    case p(PATH_DASHBOARD.executive.orgs):
    case p(PATH_DASHBOARD.executive.viewOrg('')):
    case p(PATH_DASHBOARD.executive.inspections.all):
    case p(PATH_DASHBOARD.executive.inspections.mine):
    case p(PATH_DASHBOARD.executive.inspections.incomplete):
    case p(PATH_DASHBOARD.executive.inspections.view('')):
    case p(PATH_DASHBOARD.executive.inspections.efg):
    case p(PATH_DASHBOARD.executive.inspections.fro):
    case p(PATH_DASHBOARD.executive.complaints.all):
    case p(PATH_DASHBOARD.executive.complaints.mine):
    case p(PATH_DASHBOARD.executive.complaints.incomplete):
    case p(PATH_DASHBOARD.executive.complaints.view('')):
    case p(PATH_DASHBOARD.executive.complaints.form):
    case p(PATH_DASHBOARD.executive.nov.recommendedReport):
    case p(PATH_DASHBOARD.executive.nov.issuedReport):
    case p(PATH_DASHBOARD.executive.penalties.log):
    case p(PATH_DASHBOARD.executive.penalties.recommended):
    case p(PATH_DASHBOARD.executive.penalties.history):
    case p(PATH_DASHBOARD.executive.scheduling.appointments):
    case p(PATH_DASHBOARD.executive.scheduling.appointmentNew):
    case p(PATH_DASHBOARD.executive.scheduling.appointmentView('')):
    case p(PATH_DASHBOARD.executive.scheduling.appointmentEdit('')):
    case p(PATH_DASHBOARD.executive.scheduling.calendar):
    case p(PATH_DASHBOARD.executive.contactLog):
    case p(PATH_DASHBOARD.executive.activityLog):
      return { plan: NONE, user: [ADMIN, STAFF], type: [EXECUTIVE] };

    // executive users - Staff/Admin only (donations, reports, geozones)
    case p(PATH_DASHBOARD.executive.donations.all):
    case p(PATH_DASHBOARD.executive.donations.incoming):
    case p(PATH_DASHBOARD.executive.donations.internal):
    case p(PATH_DASHBOARD.executive.donations.outgoing):
    case p(PATH_DASHBOARD.executive.geozones):
    case p(PATH_DASHBOARD.executive.inspections.reports.fro):
    case p(PATH_DASHBOARD.executive.inspections.reports.efg):
    case p(PATH_DASHBOARD.executive.complaints.report):
    case p(PATH_DASHBOARD.executive.contactSummary):
    case p(PATH_DASHBOARD.executive.complianceSummary):
      return { plan: NONE, user: [ADMIN, STAFF], type: [EXECUTIVE] };

    // executive users - Staff/Admin access (create org allowed)
    case p(PATH_DASHBOARD.executive.createOrg):
      return { plan: NONE, user: [ADMIN, STAFF], type: [EXECUTIVE] };

    case p(PATH_DASHBOARD.executive.orgSettings):
      return { plan: NONE, user: [ADMIN], type: [EXECUTIVE] };

    // "corporate" onboarding is used by both corporate and executive accounts
    case p(PATH_DASHBOARD.corporate.onboarding.root):
      return {
        plan: FREE,
        user: [ADMIN, STAFF],
        type: [BUSINESS, CORPORATE, EXECUTIVE],
      };

    // corporate users
    case p(PATH_DASHBOARD.corporate.home):
    case p(PATH_DASHBOARD.corporate.recipients):
    case p(PATH_DASHBOARD.corporate.teams):
    case p(PATH_DASHBOARD.corporate.viewTeam('')):
    case p(PATH_DASHBOARD.corporate.donations):
    case p(PATH_DASHBOARD.corporate.schedule):
    case p(PATH_DASHBOARD.corporate.viewDonation('')):
    case p(PATH_DASHBOARD.corporate.report):
    case p(PATH_DASHBOARD.corporate.nonDonatingLocationsReport):
    case p(PATH_DASHBOARD.corporate.taxReceipts):
      return { plan: FREE, user: [ADMIN, STAFF], type: [BUSINESS, CORPORATE] };
    case p(PATH_DASHBOARD.inventory):
      return {
        plan: BASIC_OR_FOODMOVER,
        user: [ADMIN, STAFF],
        type: [BUSINESS, CORPORATE, NPO],
      };
    case p(PATH_DASHBOARD.wasteTracking):
      return {
        plan: FREE,
        user: [ADMIN, STAFF],
        type: [BUSINESS, CORPORATE, NPO],
      };
    case p(PATH_DASHBOARD.corporate.donorTeamOnboardingReport):
    case p(PATH_DASHBOARD.corporate.wasteReport):
    case p(PATH_DASHBOARD.corporate.cancellationsReport):
      return { plan: FREE, user: [ADMIN, STAFF], type: [CORPORATE] };
    case p(PATH_DASHBOARD.corporate.orgSettings):
      return { plan: NONE, user: [ADMIN], type: [CORPORATE] };
    case p(PATH_DASHBOARD.settings.tags):
      return { plan: NONE, user: [ADMIN, STAFF], type: [CORPORATE, EXECUTIVE] };
    case p(PATH_DASHBOARD.invitations):
    case path === 'https://help.careit.com':
      return {
        plan: FREE,
        user: [ADMIN, STAFF, DRIVER],
        type: [...ALL, EXECUTIVE, CORPORATE],
      };

    // all users incl executive
    case p(PATH_DASHBOARD.settings.account('')):
      return {
        plan: FREE,
        user: [SUPER_ADMIN, ADMIN, STAFF, DRIVER],
        type: [...ALL, EXECUTIVE],
      };

    // basic - staff and admin
    // this has to go last becase it is the root path, otherwise we match all dashboard paths
    case p(PATH_DASHBOARD.corporateOnboarding.request('')):
    case p('/dashboard/corp-onboarding/corp/'):
      return {
        plan: FREE,
        user: [ADMIN],
        type: [NPO, BUSINESS],
      };

    case p(PATH_DASHBOARD.corporateOnboarding.complete.form('')):
    case p(PATH_DASHBOARD.corporateOnboarding.complete.agreement('')):
    case p(PATH_DASHBOARD.corporateOnboarding.complete.document('')):
    case p(PATH_DASHBOARD.corporateOnboarding.complete.upload('')):
      return {
        plan: FREE,
        user: [ADMIN, STAFF],
        type: [NPO, BUSINESS],
      };

    case p(PATH_DASHBOARD.corporateOnboarding.view.form('')):
    case p(PATH_DASHBOARD.corporateOnboarding.view.agreement('')):
    case p(PATH_DASHBOARD.corporateOnboarding.view.document('')):
    case p(PATH_DASHBOARD.corporateOnboarding.view.upload('')):
      return {
        plan: FREE,
        user: [ADMIN],
        type: [BUSINESS],
      };

    // Tax receipts - redirect (allows all types, redirects to correct page)
    // Must be before PATH_DASHBOARD.root to avoid matching /dashboard first
    case p(PATH_DASHBOARD.taxReceiptsRedirect):
      return {
        plan: FREE,
        user: [ADMIN, STAFF],
        type: [NPO, BUSINESS, CORPORATE, EXECUTIVE],
      };

    // Tax receipts - business/donor (paid plans only, handled in component)
    // Must be before PATH_DASHBOARD.root to avoid matching /dashboard first
    case p(PATH_DASHBOARD.taxReceipts):
      return {
        plan: FREE,
        user: [ADMIN],
        type: [BUSINESS],
      };

    // Tax receipts - food mover
    // Must be before PATH_DASHBOARD.root to avoid matching /dashboard first
    case p(PATH_DASHBOARD.foodMoverTaxReceipts):
      return {
        plan: FOODMOVER_FULL,
        user: [ADMIN],
        type: [NPO],
      };

    case p(PATH_DASHBOARD.cancellationReport):
      return {
        plan: FREE,
        user: [STAFF, ADMIN, DRIVER],
        type: [NPO, BUSINESS],
      };

    case p(PATH_DASHBOARD.freshcue.manageMembers):
    case p(PATH_DASHBOARD.freshcue.donors):
    case p(PATH_DASHBOARD.freshcue.donorStats):
      return {
        plan: FRESHCUE,
        user: [STAFF, ADMIN],
        type: [NPO],
        orHasOrgFlag: 'freshcue_enabled',
        requiresRecipientEin: true,
      };

    case p(PATH_DASHBOARD.freshcue.allPosts):
      return {
        plan: FRESHCUE,
        user: [STAFF, ADMIN],
        type: [NPO],
        orHasOrgFlag: 'freshcue_enabled',
        requiresRecipientEin: true,
      };

    case p(PATH_DASHBOARD.freshcue.businessPosts):
      return {
        plan: BASIC_OR_FOODMOVER_FULL,
        user: [STAFF, ADMIN],
        type: [BUSINESS, NPO],
        orHasOrgFlag: 'freshcue_donor_enabled',
        requiresRecipientEin: true,
      };

    case p(PATH_DASHBOARD.freshcue.postDetails(':postId')):
      return {
        plan: BASIC_OR_FOODMOVER_FULL,
        user: [STAFF, ADMIN],
        type: [BUSINESS, NPO],
        orHasOrgFlag: 'freshcue_donor_enabled',
        requiresRecipientEin: true,
      };

    case p(PATH_DASHBOARD.freshcue.businessReservations):
      return {
        plan: BASIC_OR_FOODMOVER_FULL,
        user: [STAFF, ADMIN],
        type: [BUSINESS, NPO],
        orHasOrgFlag: 'freshcue_donor_enabled',
        requiresRecipientEin: true,
      };

    // basic - staff and admin
    // this has to go last becase it is the root path, otherwise we match all dashboard paths
    case p(PATH_DASHBOARD.root):
      return {
        plan: FREE,
        user: [STAFF, ADMIN, DRIVER, SUPER_ADMIN],
        type: [NPO, BUSINESS, EXECUTIVE],
      };

    // any training route handles permissions on it's own
    case p(PATH_TRAINING.root):
      return {
        plan: FOODMOVER_FULL,
        user: [STAFF, ADMIN, DRIVER],
        type: [NPO, BUSINESS],
      };

    default:
      throw new Error('Unreachable');
  }
};

const policyForAction = (action) => {
  switch (action) {
    case 'export':
      return {
        plan: BASIC_OR_FOODMOVER,
        user: [STAFF, ADMIN, DRIVER, SUPER_ADMIN],
        type: [NPO, BUSINESS, EXECUTIVE, CORPORATE],
      };

    case 'recurring-donation':
      return {
        plan: BASIC_OR_FOODMOVER,
        user: [STAFF, ADMIN, DRIVER],
        type: [NPO, BUSINESS],
      };

    case 'request-drivers':
      return {
        plan: BASIC_OR_FOODMOVER,
        user: [STAFF, ADMIN],
        type: [NPO],
        hasOrgFlag: 'is_sms_enabled',
      };

    default:
      throw new Error('Unreachable');
  }
};

export const getDiscriminators = (user) => {
  const currentPlan =
    getPlanBySubscriptionProductId(user.subscription_product_id) ?? 'free';
  const planActive = user.subscription_status?.toUpperCase() === 'ACTIVE';
  const plan = planActive ? currentPlan : 'free';

  const orgType = (() => {
    if (user.is_non_profit) return NPO;
    if (user.is_executive) return EXECUTIVE;
    if (user.is_corporate) return CORPORATE;
    return BUSINESS;
  })();

  const userType = (() => {
    if (user.account_type === 'ADMIN') {
      return SUPER_ADMIN;
    } else {
      if (user.is_admin) {
        return ADMIN;
      } else if (user.is_driver) {
        return DRIVER;
      } else {
        return STAFF;
      }
    }
  })();

  return { plan, orgType, userType, user };
};

const allowedByPolicy = ({ plan, orgType, userType, user }, policy) => {
  if (policy.any) {
    return policy.any.some((option) =>
      allowedByPolicy({ plan, orgType, userType, user }, option)
    );
  }

  if (userType === SUPER_ADMIN) {
    return !!policy.user.find((x) => x === userType);
  }

  if (orgType === EXECUTIVE || orgType === CORPORATE) {
    return (
      !!policy.user.find((x) => x === userType) &&
      !!policy.type.find((x) => x === orgType)
    );
  }

  if (policy.hasOrgFlag) {
    if (!user[policy.hasOrgFlag]) {
      return false;
    }
  }

  if (policy.requiresRecipientEin && isBlockedByRecipientEin(user)) {
    return false;
  }

  const planMatches = !!policy.plan.find((x) => x === plan);
  const altFlagMatches = policy.orHasOrgFlag && !!user[policy.orHasOrgFlag];

  return (
    (planMatches || altFlagMatches) &&
    !!policy.user.find((x) => x === userType) &&
    !!policy.type.find((x) => x === orgType)
  );
};

export const checkAction = (user, action) => {
  const discriminators = getDiscriminators(user);
  const policy = policyForAction(action);
  return allowedByPolicy(discriminators, policy);
};

export const checkRoute = (user, path) => {
  if (!path || !path.startsWith('/')) return true;
  const discriminators = getDiscriminators(user);
  const policy = policyForPath(path);
  return allowedByPolicy(discriminators, policy);
};

export const useCheckAction = (action) => {
  const { user } = useAuth();
  return useMemo(() => checkAction(user, action), [user, action]);
};
